/**
 * @fileoverview Organism: the mobile launch screen — logo and name, briefly.
 * @module components/organisms/SplashScreen
 */

import { useEffect, useState } from "react"

import "./SplashScreen.css"

/** Session flag so the splash plays once per visit, not on every remount. */
const SESSION_KEY = "dailypex:splash-seen"

/** Below this width the app is treated as a mobile session. */
const MOBILE_QUERY = "(max-width: 640px)"

/** How long the brand moment holds before it fades out. */
const VISIBLE_MS = 1100

/**
 * Whether the splash should play: a mobile viewport, once per session.
 *
 * @returns True the first time a mobile session opens the app.
 */
function shouldShow(): boolean {
	if (typeof window === "undefined") return false
	if (window.sessionStorage.getItem(SESSION_KEY) !== null) return false

	return window.matchMedia(MOBILE_QUERY).matches
}

/**
 * Brand moment shown once when a mobile session opens the app: the logo and
 * app name, full-screen, fading out on its own.
 *
 * @returns The rendered splash, or null once it has played or on desktop.
 *
 * @example
 * <SplashScreen />
 */
export function SplashScreen() {
	const [visible, setVisible] = useState(shouldShow)
	const [leaving, setLeaving] = useState(false)

	useEffect(() => {
		if (!visible) return

		window.sessionStorage.setItem(SESSION_KEY, "1")

		const timer = window.setTimeout(() => {
			setLeaving(true)
		}, VISIBLE_MS)

		return () => {
			window.clearTimeout(timer)
		}
	}, [visible])

	if (!visible) return null

	return (
		<div
			className={`splash${leaving ? " splash--leaving" : ""}`}
			aria-hidden="true"
			onAnimationEnd={() => {
				setVisible(false)
			}}
		>
			<img
				className="splash__logo"
				src="/favicon.svg"
				alt=""
				width={72}
				height={72}
			/>
			<p className="splash__name">DailyPex</p>
		</div>
	)
}
